import { getAttributeNames } from '@/utils/helpers';
import { isEmpty } from 'lodash/fp';

export const ATTR_WHITELIST = [
  'id',
  'name',
  'aria-required',
  'aria-describedby',
  'aria-invalid',
  'rows',
  'maxlength',
  'value',
  'checked',
];

const hasClass = (className: string) => (el: Element) =>
  new RegExp(className, 'i').test(el.classList.toString());

export const removeStyleAttr = (html = '') => {
  return html.replace(/ style="(.*?)"/g, '');
};

export const normalizeAttrName = (attrName: string) => {
  switch (attrName) {
    case 'maxlength':
      return 'maxLength';
    default:
      return attrName;
  }
};

export const containsField = (el: Element) => {
  return el.querySelector('.mktoFieldWrap, .mktoButtonWrap');
};

export const getElementType = (field: Element) => {
  if (field instanceof HTMLButtonElement || field instanceof HTMLInputElement) {
    return field.type || field.localName;
  }

  return field?.localName;
};

export const getElementLabel = (field: HTMLElement) => {
  if (field instanceof HTMLSelectElement) {
    const firstOption = field.options[0];
    if (!firstOption?.value) {
      return firstOption.innerText;
    }
  }

  if (field instanceof HTMLButtonElement) {
    return field.textContent;
  }

  if (field instanceof HTMLTextAreaElement) {
    return field.placeholder;
  }

  if (field instanceof HTMLInputElement) {
    const parent = field.closest('.mktoFieldWrap');
    const preLabel = parent?.querySelector('label')?.textContent?.replace('*', '');
    const postLabel =
      field.nextSibling instanceof HTMLLabelElement &&
      field.nextSibling.textContent !== '' &&
      removeStyleAttr(field.nextSibling.innerHTML);

    switch (field.type) {
      case 'radio':
      case 'checkbox':
        return {
          pre: preLabel,
          post: postLabel,
        };
      case 'submit':
        return field.value;
      default:
        return field.placeholder;
    }
  }

  return undefined;
};

export const getElementOptions = (field: HTMLElement) => {
  if (field instanceof HTMLSelectElement) {
    const options = Array.from(field.options) as HTMLOptionElement[];
    return options
      .map(
        (option: HTMLOptionElement) =>
          option.value && {
            value: option.value,
            disabled: option.disabled,
            label: option.innerText,
          },
      )
      .filter((option: any) => Boolean(option));
  }

  return undefined;
};

export const getElementAttributes = (field: HTMLElement) => {
  if (field.hasAttributes()) {
    const attrNames = getAttributeNames({ target: field });
    return attrNames
      .filter((attrName) => ATTR_WHITELIST.includes(attrName))
      .filter(
        (attrName) =>
          attrName !== 'value' ||
          (field instanceof HTMLInputElement && /radio|checkbox/.test(field.type)),
      )
      .map((attrName) => {
        const attr = field.getAttribute(attrName);
        return {
          [normalizeAttrName(attrName)]: /^id$/.test(attrName) ? `${attr}_2` : attr,
        };
      })
      .reduce((a, b) => ({ ...a, ...b }), {});
  }

  return {};
};

export const getElementChecked = (field: HTMLElement) => {
  if (field instanceof HTMLInputElement) {
    return field.checked;
  }

  return undefined;
};

export const getElementDisabled = (field: HTMLElement) => {
  const { disabled } = field as any;
  return disabled;
};

export const mapElementToObject = (element: HTMLElement) => ({
  type: getElementType(element),
  label: getElementLabel(element),
  options: getElementOptions(element),
  attributes: getElementAttributes(element),
  checked: getElementChecked(element),
  disabled: getElementDisabled(element),
});

export const mapItemToField = (_item: HTMLElement | Node) => {
  if (_item?.nodeType === Node.TEXT_NODE) {
    return {
      type: 'html',
      html: (_item as Node).nodeValue,
    };
  }

  const item = _item as HTMLElement;
  const elementList = item?.querySelector('.mktoCheckboxList, .mktoRadioList');
  const error = item?.querySelector('.mktoError');
  const errorMessage = error && error.querySelector('.mktoErrorMsg')?.textContent;

  if (elementList) {
    const elements = Array.from(elementList.querySelectorAll('.mktoField')) as HTMLElement[];

    return {
      type: 'options',
      options: elements.map((el) => mapElementToObject(el)),
      error: errorMessage,
    };
  }

  const element = item?.querySelector('.mktoField, .mktoButton') as HTMLElement;
  const htmlElement = item?.querySelector('.mktoHtmlText');
  const isNotYouElement = item?.classList.contains('mktoNotYou');

  if (element) {
    return {
      ...mapElementToObject(element),
      error: errorMessage,
    };
  }

  if (htmlElement) {
    return {
      type: 'html',
      html: removeStyleAttr(htmlElement?.innerHTML),
      error: errorMessage,
    };
  }

  if (isNotYouElement)
    return {
      type: 'notYou',
      html: removeStyleAttr(item?.outerHTML),
      error: errorMessage,
    };

  if (item?.outerHTML) {
    return {
      type: 'html',
      html: removeStyleAttr(item?.outerHTML),
      error: errorMessage,
    };
  }

  return undefined;
};

const mapMktoTemplateBoxNodes = (nodes: ChildNode[]) => {
  const concatHtmlElements = (previousMappedFields: any[], mappedField: any) => {
    const originalPreviousMappedFields = [...previousMappedFields];
    const lastMappedField = !isEmpty(originalPreviousMappedFields)
      ? originalPreviousMappedFields.pop()
      : undefined;

    if (mappedField?.type === 'html' && lastMappedField?.type === 'html') {
      return [
        ...originalPreviousMappedFields,
        {
          ...lastMappedField,
          html: lastMappedField?.html + mappedField?.html,
        },
      ];
    }
    return [...previousMappedFields, mappedField];
  };

  return nodes
    .map((nodeElement) => {
      try {
        return mapItemToField(nodeElement);
      } catch (e) {
        console.log('Error mapping the item to field in mapMktoTemplateBoxNodes', e);
        return '';
      }
    })
    .filter(Boolean)
    .reduce(concatHtmlElements, [])
    .map((ele) => [ele]);
};

export const mapControls = (parent: HTMLElement): any[] => {
  const formChildren = Array.from(parent.children);
  const mktoTemplateBox = formChildren.filter(hasClass('mktoTemplateBox'))[0] as HTMLElement;

  if (mktoTemplateBox) return mapMktoTemplateBoxNodes(Array.from(mktoTemplateBox?.childNodes));

  const hasMktoRows = (el: Element) => /row/i.test(el.classList.toString()) && containsField(el);
  const mktoContentRows = formChildren.filter(hasMktoRows) as HTMLElement[];

  return mktoContentRows
    .map((rowEl) => {
      const formColumns = Array.from(rowEl.children).filter((el) =>
        /col/i.test(el.classList.toString()),
      ) as HTMLElement[];

      if (!formColumns.length) return [mapItemToField(rowEl)];

      return formColumns
        .map((colEl) => {
          const hasRows = Array.from(colEl.querySelectorAll('.mktoFormRow')) as HTMLElement[];
          const isFieldSet = colEl instanceof HTMLFieldSetElement;

          if (isFieldSet) {
            return {
              type: 'fieldset',
              legend: isFieldSet && colEl?.querySelector('legend')?.textContent,
              controls: hasRows.length ? mapControls(colEl) : mapItemToField(colEl),
            };
          }

          return hasRows.length ? mapControls(colEl) : mapItemToField(colEl);
        })
        .filter((item) => item) as any[];
    })
    .filter((item) => item) as any[];
};
